// Environment configuration utility
// This file centralizes all environment variable access for the 

const config = {
  // Frontend Configuration - DevOps configurable
  FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || `http://${process.env.REACT_APP_HOST || 'localhost'}:${process.env.PORT || 3000}`,
  FRONTEND_HOST: process.env.REACT_APP_HOST || 'localhost',
  FRONTEND_PORT: parseInt(process.env.PORT) || 3000,
  
  // Backend API Configuration - Fully DevOps configurable
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || `http://${process.env.REACT_APP_BACKEND_HOST || 'localhost'}:${process.env.REACT_APP_BACKEND_PORT || 5001}`,
  BACKEND_HOST: process.env.REACT_APP_BACKEND_HOST || 'localhost',
  BACKEND_PORT: parseInt(process.env.REACT_APP_BACKEND_PORT) || 5001,
  API_BASE_PATH: process.env.REACT_APP_API_BASE_PATH || '/api',
  
  // Computed API URL - Automatically adapts to DevOps changes
  get API_BASE_URL() {
    return `${this.BACKEND_URL}${this.API_BASE_PATH}`;
  },
  
  // Legacy support (for existing code)
  get API_URL() {
    return this.API_BASE_URL;
  },
  
  // Supabase configuration - DevOps configurable
  SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL,
  SUPABASE_ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY,
  
  // Application Configuration - DevOps customizable
  APP_NAME: process.env.REACT_APP_NAME || 'AttenZef',
  APP_VERSION: process.env.REACT_APP_VERSION || '2.0.0',
  
  // Environment - DevOps controlled
  NODE_ENV: process.env.NODE_ENV || 'development',
  ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development',
  
  // Feature Flags - DevOps toggleable
  ENABLE_DEBUG_LOGS: process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true',
  ENABLE_API_LOGGING: process.env.REACT_APP_ENABLE_API_LOGGING === 'true',
  ENABLE_DEVOPS_INFO: process.env.REACT_APP_ENABLE_DEVOPS_INFO === 'true' || process.env.NODE_ENV === 'development',
  
  // DevOps Deployment Information
  DEPLOYMENT_TIMESTAMP: process.env.REACT_APP_DEPLOYMENT_TIMESTAMP || new Date().toISOString(),
  BUILD_VERSION: process.env.REACT_APP_BUILD_VERSION || 'dev-build',
  DEPLOYED_BY: process.env.REACT_APP_DEPLOYED_BY || 'local-dev',
  
  // Environment helpers
  get IS_DEVELOPMENT() {
    return this.ENVIRONMENT === 'development';
  },
  
  get IS_PRODUCTION() {
    return this.ENVIRONMENT === 'production';
  },
  
  get IS_STAGING() {
    return this.ENVIRONMENT === 'staging';
  },
  
  // Helper methods
  getApiUrl(endpoint) {
    // Remove leading slash if present to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${this.API_BASE_URL}${cleanEndpoint}`;
  },
  
  getBackendUrl(path = '') {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.BACKEND_URL}${cleanPath}`;
  },
  
  // Validation method
  validate() {
    const required = {
      BACKEND_URL: this.BACKEND_URL,
      SUPABASE_URL: this.SUPABASE_URL,
      SUPABASE_ANON_KEY: this.SUPABASE_ANON_KEY
    };
    
    const missing = [];
    const warnings = [];
    
    // Check required variables
    Object.entries(required).forEach(([key, value]) => {
      if (!value || value.includes('your_') || value.includes('your-project')) {
        missing.push(key);
      }
    });
    
    // Check for localhost in production
    if (this.IS_PRODUCTION) {
      if (this.BACKEND_URL.includes('localhost')) {
        warnings.push('BACKEND_URL contains localhost in production environment');
      }
      if (this.FRONTEND_URL.includes('localhost')) {
        warnings.push('FRONTEND_URL contains localhost in production environment');
      }
    }
    
    // Log results
    if (missing.length > 0) {
      console.error('❌ Missing or invalid environment variables:', missing);
      console.error('Please check your .env file and ensure all required variables are set.');
    }
    
    if (warnings.length > 0 && this.ENABLE_DEBUG_LOGS) {
      console.warn('⚠️ Environment warnings:', warnings);
    }
    
    return missing.length === 0;
  },
  
  // Display current configuration
  displayConfig() {
    if (!this.ENABLE_DEBUG_LOGS && !this.IS_DEVELOPMENT) return;
    
    console.log('\n🔧 Frontend Environment Configuration:');
    console.log('='.repeat(50));
    console.log(`App: ${this.APP_NAME} v${this.APP_VERSION}`);
    console.log(`Environment: ${this.ENVIRONMENT.toUpperCase()}`);
    console.log(`Frontend URL: ${this.FRONTEND_URL}`);
    console.log(`Backend URL: ${this.BACKEND_URL}`);
    console.log(`API Base URL: ${this.API_BASE_URL}`);
    console.log(`Supabase Configured: ${!!(this.SUPABASE_URL && this.SUPABASE_ANON_KEY)}`);
    console.log(`Debug Logs: ${this.ENABLE_DEBUG_LOGS ? 'ENABLED' : 'DISABLED'}`);
    console.log(`API Logging: ${this.ENABLE_API_LOGGING ? 'ENABLED' : 'DISABLED'}`);
    console.log('='.repeat(50));
  }
};

// Validate and display configuration on import
config.displayConfig();
const isValid = config.validate();

if (!isValid && config.IS_DEVELOPMENT) {
  console.log('\n💡 To fix missing environment variables:');
  console.log('1. Check your .env file in the web folder');
  console.log('2. Ensure all REACT_APP_ prefixed variables are set');
  console.log('3. Restart the development server after changes');
}

export default config;
